import { useState } from 'react';
import type { ProduktyPrehledStats } from '../api/rondo';

export const COUNTRY_NAME: Record<string, string> = {
  CZ: 'Česko',
  SK: 'Slovensko',
  RO: 'Rumunsko',
};

export const intFmt = (n: number) => n.toLocaleString('cs-CZ');

export function dayFmt(iso: string): string {
  return new Date(iso).toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function dayShort(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}. ${d.getMonth() + 1}.`;
}

export function Kpi({ label, value, sub, dot }: { label: string; value: string; sub?: string; dot: string }) {
  return (
    <div className="col-6 col-md-3">
      <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 8, padding: '12px 14px' }}>
        <div className="d-flex align-items-center gap-2 mb-1">
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block', flexShrink: 0 }} />
          <div className="text-uppercase text-muted" style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.034em' }}>{label}</div>
        </div>
        <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#6f6f6f', lineHeight: 1.2 }}>{value}</div>
        {sub != null && <div className="text-muted" style={{ fontSize: '0.72rem' }}>{sub}</div>}
      </div>
    </div>
  );
}

export function CountryTrendAndTable({ stats }: { stats: ProduktyPrehledStats }) {
  return (
    <>
      <div className="row g-3 mb-3">
        <div className="col-lg-5">
          <div className="card card-border h-100">
            <div className="card-header card-header-action">
              <h6 className="card-title mb-0">Spinů per země (30 dní)</h6>
              {stats.latestSnapshot && (
                <span className="text-muted small">snapshot {dayFmt(stats.latestSnapshot)}</span>
              )}
            </div>
            <div className="card-body">
              {stats.byCountry.map((c, i) => (
                <div
                  key={c.country}
                  className="d-flex align-items-center gap-3 py-2"
                  style={i < stats.byCountry.length - 1 ? { borderBottom: '1px solid #eef0f3' } : undefined}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '1rem' }}>
                      {COUNTRY_NAME[c.country] ?? c.country}{' '}
                      <span className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 500 }}>({c.country})</span>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                      {intFmt(c.uniqueUsers)} hráčů (Σ 30d)
                    </div>
                  </div>
                  <div className="ms-auto fw-semibold" style={{ fontSize: '0.95rem' }}>{intFmt(c.plays)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card card-border h-100">
            <div className="card-header card-header-action">
              <h6 className="card-title mb-0">Denní trend (30 dní)</h6>
              <span className="text-muted small">CZ · SK · RO</span>
            </div>
            <div className="card-body">
              {stats.trend30d.length === 0 ? (
                <div className="text-muted py-3">Žádná data.</div>
              ) : (
                <TrendLineChart points={stats.trend30d} />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card card-border mb-0">
        <div className="card-body p-0">
          <div className="table-responsive-mobile"><table className="table aff-list-table sticky-thead align-middle mb-0">
            <thead>
              <tr>
                <th>Den</th>
                <th className="text-end">CZ</th>
                <th className="text-end">SK</th>
                <th className="text-end">RO</th>
                <th className="text-end">Σ celkem</th>
              </tr>
            </thead>
            <tbody>
              {stats.detail14d.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted py-4">Žádná data.</td></tr>
              ) : (
                stats.detail14d.map((d) => (
                  <tr key={d.date} className="aff-row">
                    <td className="fw-semibold">{dayFmt(d.date)}</td>
                    <td className="text-end">{intFmt(d.cz)}</td>
                    <td className="text-end">{intFmt(d.sk)}</td>
                    <td className="text-end">{intFmt(d.ro)}</td>
                    <td className="text-end fw-semibold">{intFmt(d.total)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table></div>
        </div>
      </div>
    </>
  );
}

export function TrendLineChart({ points }: { points: { date: string; cz: number; sk: number; ro: number; total: number }[] }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const W = 760;
  const H = 240;
  const PAD = { top: 12, right: 12, bottom: 24, left: 48 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const max = Math.max(1, ...points.flatMap((p) => [p.cz, p.sk, p.ro]));
  const n = points.length;
  const x = (i: number) => PAD.left + (n <= 1 ? 0 : (i / (n - 1)) * innerW);
  const y = (v: number) => PAD.top + innerH - (v / max) * innerH;
  const line = (key: 'cz' | 'sk' | 'ro') =>
    points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ');
  const series: { key: 'cz' | 'sk' | 'ro'; label: string; color: string }[] = [
    { key: 'cz', label: 'CZ', color: '#1d4ed8' },
    { key: 'sk', label: 'SK', color: '#137333' },
    { key: 'ro', label: 'RO', color: '#ea7a00' },
  ];
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({ v: max * t, yy: PAD.top + innerH - t * innerH }));
  const fmtTick = (v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v)));
  const hp = hovered == null ? null : (points[hovered] ?? null);
  const tooltipX = hovered == null ? 0 : Math.min(W - 172, Math.max(PAD.left + 6, x(hovered) - 78));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', overflow: 'visible' }} preserveAspectRatio="xMidYMid meet" onMouseLeave={() => setHovered(null)}>
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={PAD.left} y1={t.yy} x2={W - PAD.right} y2={t.yy} stroke="#eef0f3" strokeWidth={1} />
          <text x={PAD.left - 8} y={t.yy + 3} textAnchor="end" fontSize={10} fill="#9ca3af">{fmtTick(t.v)}</text>
        </g>
      ))}
      {series.map((s) => (
        <path key={s.key} d={line(s.key)} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      ))}
      {hp && hovered != null && <g pointerEvents="none">
        <line x1={x(hovered)} y1={PAD.top} x2={x(hovered)} y2={PAD.top + innerH} stroke="#aeb4b8" strokeWidth="1" strokeDasharray="3 4" />
        {series.map((s)=><circle key={s.key} cx={x(hovered)} cy={y(hp[s.key])} r="4" fill="#fff" stroke={s.color} strokeWidth="2" />)}
        <rect x={tooltipX} y={14} width="166" height="88" rx="10" fill="#202225" opacity=".96" />
        <text x={tooltipX+12} y={32} fill="#c9ced1" fontSize="9">{dayFmt(hp.date)}</text>
        <text x={tooltipX+12} y={51} fill="#fff" fontSize="10" fontWeight="700">Celkem {intFmt(hp.total)}</text>
        <text x={tooltipX+12} y={69} fill="#8fb0ff" fontSize="9">CZ {intFmt(hp.cz)}</text>
        <text x={tooltipX+66} y={69} fill="#8dd1a6" fontSize="9">SK {intFmt(hp.sk)}</text>
        <text x={tooltipX+120} y={69} fill="#ffc47f" fontSize="9">RO {intFmt(hp.ro)}</text>
      </g>}
      {points.map((_,i)=>{
        const left=i===0?PAD.left:(x(i-1)+x(i))/2;
        const right=i===points.length-1?W-PAD.right:(x(i)+x(i+1))/2;
        return <rect key={i} x={left} y={PAD.top} width={Math.max(1,right-left)} height={innerH} fill="transparent" onMouseEnter={()=>setHovered(i)} />;
      })}
    </svg>
  );
}
